import { helpListItemStyleDark, helpListItemStyleLight } from "./colors.help";
import type { DefaultTheme } from "styled-components";
import type { RecorderType } from "utils/RecorderUtil";
import {
  DISCOUNT_TIER,
  SUBSCRIPTION_STATUS,
} from "api/interfaces/subscription.interface";

export type ObjType = {
  [index: string]: string | any;
};

export const COLORS = {
  PRIMARY1: "#273457",
  PRIMARY2: "#141E2E",
  PRIMARY3: "#252E44",
  BUTTONS1: "#7B88A8",
  BUTTONS2: "#008AFF",
  SELECTED: "#2EE3FF",
  PROGRESSBAR: "#FB471F",
  WHITE: "#FFFFFF",
  BLACK: "#000000",
  RED: "#FF0000",
  SIDEBARBG: "#0A121E",
  COMPANY: "#ED7422",
  BORDER: "#535D76",
  TOGGLE: "#545E77",
  TOGGLE_ACTIVE: "#1C75C4",
  ORANGE: "#D55F1B",
  GREEN: "#00E286",
  VALIDATERED: "#FF2E2E",
  LIGHTGRAY: "#abb5c5",
  SIDEBARMENU: "#85898F",
  GRAY: "#7883A1",
  WARNING: "#FFB013",
  PAGE_TITLE: "#222",
  TEXT: "#666",
  HELP: helpListItemStyleDark,
};

export const LIGHT_THEME_COLORS = {
  BACKGROUND: COLORS.WHITE,
  SIDEBARBG: COLORS.WHITE,
  HEADERBG: COLORS.WHITE,
  LIGHTGRAYBORDER: "#EDEDED",
  LIGHTGRAY: "#F0F0F0",
  WHITE: "#000000",
  BUTTONS1: "#ED7422",
  BUTTONS2: "#ED7422",
  BUTTONS3: "#7b88a8",
  STATUS: "#44546F",
  FILTERITEM: "#f8f8f8",
  SELECTED: "#ED7422",
  // WIDGETCOLOR: "#FFFFFF",
  WIDGETCOLOR: "#F5F5F5",
  PRIMARY2: COLORS.WHITE,
  PRIMARY3: "#F5F5F5",
  BORDER: "#cacaca",
  PAGE_TITLE: "#222",
  TEXT: "#666",
  HELP: helpListItemStyleLight,
  TOGGLE: "#ED7422",
  DISABLED: "rgba(202, 202, 202, 0.15)",
  DISABLED_BORDER: "#cacaca",
  GREEN: "#0ebd39",
  CARD_BG: "rgba(245, 245, 245, 0.376)",
  CARD_BORDER:"#EDEDED",
};

export const DARK_THEME_COLORS = {
  BACKGROUND: COLORS.PRIMARY2,
  SIDEBARBG: COLORS.SIDEBARBG,
  HEADERBG: COLORS.PRIMARY3,
  LIGHTGRAYBORDER: "#535D76",
  BUTTONS3: "#545E77",
  STATUS: "#b5c5e1",
  SELECTED: "#2EE3FF",
  FILTERITEM: "#20283b",
  WIDGETCOLOR: "#0c1724",
  PAGE_TITLE: "#FFF",
  TEXT: "#999",
  HELP: helpListItemStyleDark,
  LIGHTGRAY: "#c3c9d8",
  BORDER: "#333333",
  TOGGLE: "#1C75C4",
  DISABLED: "rgba(130, 140, 158, 0.15)",
  DISABLED_BORDER: "rgba(130, 140, 158, 1)",
  CARD_BG: "rgb(37 46 68)",
  CARD_BORDER: "rgb(83 93 118)",
};

export const BACKGROUND_COLORS = [
  {
    Blue: COLORS.BUTTONS2,
  },
  { Green: COLORS.GREEN },
  { Red: COLORS.RED },
  { Purple: "#8000FF" },
  { Orange: COLORS.ORANGE },
];

export const NOTICE_COLORS = {
  BLUE: COLORS.BUTTONS2,
  GREEN: COLORS.GREEN,
  PURPLE: "#8000FF",
  ORANGE: COLORS.ORANGE,
  RED: COLORS.RED,
  COOKIE: "#1B2332",
  LIGHTCOOKIE: "#FFF6F0",
};
export const MAP_STATUS_COLORS: ObjType = {
  HEALTHY: "#0BA775",
  WARNING: COLORS.WARNING,
  ALERT: "#ff4800",
  ASSIGNED: COLORS.SELECTED,
  RESOLVED: COLORS.GREEN,
  CRITICAL: "#cc2904",
};

export const ALERT_CHART_COLORS: ObjType = {
  WARNING: COLORS.WARNING,
  ALERT: "#ff4800",
  ASSIGNED: "#03AAFB",
  RESOLVED: COLORS.GREEN,
  CRITICAL: MAP_STATUS_COLORS.CRITICAL,
};

export const LEVELS: ObjType = {
  DW: COLORS.COMPANY,
  CP: "#8875FF",
  SP: "#8875FF",
  EU: "#336699",
};

export type IncreaseString = "INCREASE" | "DECREASE" | "NONE";

export const INDECREASECOLORS: { [key in IncreaseString]: string } = {
  INCREASE: COLORS.GREEN,
  DECREASE: COLORS.PROGRESSBAR,
  NONE: COLORS.BUTTONS2,
};

export const tierColor = (tier: DISCOUNT_TIER, theme: DefaultTheme) => {
  switch (tier) {
    case DISCOUNT_TIER.PLATINUM:
      return "#BBD6FF";
    case DISCOUNT_TIER.GOLD:
      return "#F8C71C";
    case DISCOUNT_TIER.SILVER:
      return "#A7B2C4";
    case DISCOUNT_TIER.CUSTOM:
      return theme.COLORS.SELECTED;
    case DISCOUNT_TIER.AUTHORIZED:
      return theme.COLORS.BUTTONS2;
    default:
      return theme.COLORS.WHITE;
  }
};

export const statusToColor = (status: string) => {
  const statusTxt = status.toUpperCase();
  switch (statusTxt) {
    case "RESOLVED":
      return COLORS.GREEN;
    case "ALERT":
      return COLORS.PROGRESSBAR;
    case "ACTIVE":
      return COLORS.PROGRESSBAR;
    case "WARNING":
      return COLORS.WARNING;
    case "ASSIGNED":
      return COLORS.SELECTED;
    default:
      return COLORS.BUTTONS1;
  }
};

export const getRecorderColor = ({
  theme,
  type,
}: {
  theme: DefaultTheme;
  type: RecorderType;
}) => {
  if (type.includes("NVR") || type.includes("VMAX")) {
    return theme.COLORS.PRIMARY1;
  }
  if (type.toUpperCase() === "SPECTRUM") {
    return "#175B22";
  }
  if (type.toUpperCase() === "COVA") {
    return NOTICE_COLORS.PURPLE;
  }
  return `${theme.COLORS.WHITE}40`;
};

export const switchStatusColor = (
  status: SUBSCRIPTION_STATUS | string = "",
  theme: string
) => {
  switch (status.toUpperCase()) {
    case "OK":
      return COLORS.GREEN;
    case "EXPIRES SOON":
      return COLORS.WARNING;
    case "EXPIRED":
      return COLORS.VALIDATERED;
    case "ERROR":
      return COLORS.RED;
    case SUBSCRIPTION_STATUS.ACTIVATED:
      return COLORS.GREEN;
    case SUBSCRIPTION_STATUS.EXPIRED_WARNING:
      return COLORS.WARNING;
    case SUBSCRIPTION_STATUS.EXPIRED_SOON:
      return LIGHT_THEME_COLORS.BUTTONS2;
    case SUBSCRIPTION_STATUS.EXPIRED:
      return COLORS.VALIDATERED;
    case SUBSCRIPTION_STATUS.EXPIRED_END:
      return COLORS.VALIDATERED;
    default:
      return theme === "light" ? COLORS.BLACK : COLORS.WHITE;
  }
};
